import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography
} from "@mui/joy";
import { createRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { useOnboardingStatus } from "../../shared/api/tenant.hooks";
import { useLogOut } from "../../shared/hook";
import { useRoleBasedNavigation } from "../../shared/hook/useRoleBasedNavigation";
import { useOnboardingStore } from "../../shared/store";
import { onboardingRootRoute } from "./onboarding-root";

/** Maps user roles to human-readable stage names for the completion heading. */
export function getStageNames(userRoles: string[]): string {
  const roleToName: Record<string, string> = {
    "workday-admin": "Workday",
    "it-admin": "Domain",
    "sso-admin": "SSO"
  };
  const names = userRoles
    .filter((r) => roleToName[r])
    .map((r) => roleToName[r]);
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

const CompletionPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    isWorkdayAdmin,
    isItAdmin,
    isSsoAdmin,
    getPrevious,
    accessiblePages,
    roles,
    allStagesComplete
  } = useRoleBasedNavigation();

  const stageNames = getStageNames(roles as string[]);
  const logout = useLogOut();

  // Get UUID from the onboarding store (set during login)
  const { uuid } = useOnboardingStore();

  // Fetch fresh data from hub-api - this is the SINGLE SOURCE OF TRUTH
  // No sessionStorage, no fallbacks, no local calculations
  const { data: statusResponse, isLoading, error } = useOnboardingStatus(uuid);

  // Extract data from API response
  const apiData = statusResponse?.data;
  const onboardingData = apiData?.onboardingData || {};
  const apiEnvironment = apiData?.environment;

  // Use API-provided values ONLY - no fallbacks
  const roosterUrl = apiEnvironment?.schedulerUrl;
  const effectiveRegion = apiEnvironment?.region;
  const targetEnvironment = apiEnvironment?.targetEnvironment;

  // Use the centralized logout hook for proper cleanup
  const handleFinish = async () => {
    await logout();
  };

  // Get the previous page for the Back button based on user's accessible pages
  const previousPage = getPrevious("/onboarding/completion");
  const previousPageConfig = accessiblePages.find(
    (p) => p.path === previousPage
  );
  const previousPageLabel = previousPageConfig?.label || "Back";

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4, textAlign: "center" }}>
        {allStagesComplete ? (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: "success.500", mb: 2 }}
            />

            <Typography level="h2" sx={{ mb: 3 }}>
              Onboarding Complete!
            </Typography>

            <Alert color="success" sx={{ mb: 3 }}>
              <Typography level="body-md">
                Thank you for completing the Rooster onboarding process. Your
                configuration has been submitted and your tenant is ready for
                use.
              </Typography>
            </Alert>

            {/* Access Information Card */}
            {roosterUrl && (
              <Card variant="soft" color="primary" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography level="h4" sx={{ mb: 2 }}>
                    🚀 Ready to Get Started?
                  </Typography>
                  <Typography level="body-md" sx={{ mb: 2 }}>
                    Your Rooster application is now available. Click the link
                    below to access your tenant:
                  </Typography>
                  <Button
                    component="a"
                    href={roosterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="lg"
                    endDecorator={<LaunchIcon />}
                    sx={{ mb: 2, minWidth: 250 }}
                  >
                    Access Rooster Application
                  </Button>
                  <Typography level="body-sm">
                    <Link
                      href={roosterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {roosterUrl}
                    </Link>
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: "success.500", mb: 2 }}
            />

            <Typography level="h2" sx={{ mb: 3 }}>
              Your {stageNames} Configuration is Complete!
            </Typography>

            <Alert color="primary" sx={{ mb: 3 }}>
              <Typography level="body-md">
                Thank you for completing your part of the onboarding process.
                Other configuration stages are still in progress. Once all
                stages are complete, all participants will be notified by email.
              </Typography>
            </Alert>
          </>
        )}

        {/* Configuration Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography level="h4" sx={{ mb: 3 }}>
              📋 Configuration Summary
            </Typography>

            {Object.keys(onboardingData).length === 0 ||
            (!onboardingData.workday &&
              !onboardingData.domains &&
              !onboardingData.sso_configs) ? (
              <Alert color="neutral" sx={{ mb: 2 }}>
                <Typography level="body-md">
                  Your configuration has been submitted. Please contact your
                  implementation team if you need to view or modify your
                  configuration details.
                </Typography>
              </Alert>
            ) : null}

            <Stack spacing={3} sx={{ textAlign: "left" }}>
              {/* System Configuration - First */}
              <Box>
                <Typography
                  level="title-md"
                  sx={{ mb: 2, color: "primary.main" }}
                >
                  System Configuration
                </Typography>
                <Stack spacing={2}>
                  {targetEnvironment && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography level="body-md" fontWeight="bold">
                        Target Environment:
                      </Typography>
                      <Chip variant="outlined" size="md">
                        {targetEnvironment}
                      </Chip>
                    </Box>
                  )}
                  {effectiveRegion && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography level="body-md" fontWeight="bold">
                        Workday Region:
                      </Typography>
                      <Chip variant="outlined" size="md">
                        {effectiveRegion}
                      </Chip>
                    </Box>
                  )}
                  {(apiEnvironment?.zoomConnected ||
                    onboardingData.zoom_connected) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <Typography level="body-md" fontWeight="bold">
                        Zoom Integration:
                      </Typography>
                      <Chip variant="outlined" size="md">
                        {apiEnvironment?.zoomConnected ||
                          onboardingData.zoom_connected}
                      </Chip>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Workday Integration - Only visible to Workday Admins */}
              {isWorkdayAdmin && onboardingData.workday && (
                <>
                  <Divider />
                  <Box>
                    <Typography
                      level="title-md"
                      sx={{ mb: 2, color: "primary.main" }}
                    >
                      Workday Integration
                    </Typography>
                    <Stack spacing={2}>
                      {onboardingData.workday.tenant && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Typography level="body-md" fontWeight="bold">
                            Tenant ID:
                          </Typography>
                          <Chip variant="outlined" size="md">
                            {onboardingData.workday.tenant}
                          </Chip>
                        </Box>
                      )}
                      {onboardingData.workday.url && (
                        <Box>
                          <Typography
                            level="body-md"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                          >
                            Web Service Base URI:
                          </Typography>
                          <Typography
                            level="body-sm"
                            sx={{
                              wordBreak: "break-all",
                              bgcolor: "background.level1",
                              p: 1,
                              borderRadius: "sm"
                            }}
                          >
                            {onboardingData.workday.url}
                          </Typography>
                        </Box>
                      )}
                      {onboardingData.workday.app_url && (
                        <Box>
                          <Typography
                            level="body-md"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                          >
                            Web Application URL:
                          </Typography>
                          <Typography
                            level="body-sm"
                            sx={{
                              wordBreak: "break-all",
                              bgcolor: "background.level1",
                              p: 1,
                              borderRadius: "sm"
                            }}
                          >
                            {onboardingData.workday.app_url}
                          </Typography>
                        </Box>
                      )}
                      {onboardingData.workday.authMethod && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Typography level="body-md" fontWeight="bold">
                            Authentication Method:
                          </Typography>
                          <Chip variant="soft" color="neutral" size="md">
                            {onboardingData.workday.authMethod === "oauth"
                              ? "OAuth 2.0"
                              : "Account / Password"}
                          </Chip>
                        </Box>
                      )}
                      {typeof onboardingData.workday.wql === "boolean" && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Typography level="body-md" fontWeight="bold">
                            WQL Enabled:
                          </Typography>
                          <Chip
                            variant="soft"
                            color={
                              onboardingData.workday.wql ? "success" : "neutral"
                            }
                            size="md"
                          >
                            {onboardingData.workday.wql ? "Yes" : "No"}
                          </Chip>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </>
              )}

              {/* Domain List - Only visible to IT Admins */}
              {isItAdmin &&
                onboardingData.domains &&
                Array.isArray(onboardingData.domains) &&
                onboardingData.domains.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        level="title-md"
                        sx={{ mb: 2, color: "primary.main" }}
                      >
                        Domain Configuration
                      </Typography>
                      <Stack spacing={2}>
                        <Typography level="body-md" fontWeight="bold">
                          Configured Domains: {onboardingData.domains.length}
                        </Typography>
                        {onboardingData.domains.map(
                          (
                            domain: {
                              id?: string;
                              name?: string;
                              provider: string;
                              isPrimary?: boolean;
                            },
                            index: number
                          ) => (
                            <Box
                              key={domain.id || domain.name || index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                pl: 2
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1
                                }}
                              >
                                <Typography level="body-md">
                                  {domain.id || domain.name}
                                </Typography>
                                {domain.isPrimary && (
                                  <Chip
                                    variant="soft"
                                    color="primary"
                                    size="sm"
                                  >
                                    Primary
                                  </Chip>
                                )}
                              </Box>
                              <Chip variant="outlined" size="sm">
                                {domain.provider === "google"
                                  ? "Google"
                                  : domain.provider === "office365" ||
                                      domain.provider === "O365"
                                    ? "Office 365"
                                    : domain.provider}
                              </Chip>
                            </Box>
                          )
                        )}
                      </Stack>
                    </Box>
                  </>
                )}

              {/* SSO Configuration - Only visible to SSO Admins */}
              {isSsoAdmin &&
                (onboardingData.sso_configs || onboardingData.sso_provider) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        level="title-md"
                        sx={{ mb: 2, color: "primary.main" }}
                      >
                        Single Sign-On (SSO)
                      </Typography>
                      <Stack spacing={2}>
                        {onboardingData.sso_configs &&
                        Array.isArray(onboardingData.sso_configs) &&
                        onboardingData.sso_configs.length > 0 ? (
                          <>
                            <Typography level="body-md" fontWeight="bold">
                              Configured SSO Providers:{" "}
                              {onboardingData.sso_configs.length}
                            </Typography>
                            {onboardingData.sso_configs.map(
                              (config: { provider: string }) => (
                                <Box
                                  key={config.provider}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    pl: 2
                                  }}
                                >
                                  <Typography level="body-md">
                                    {config.provider.charAt(0).toUpperCase() +
                                      config.provider.slice(1)}
                                  </Typography>
                                  <Chip
                                    variant="solid"
                                    color="success"
                                    size="sm"
                                  >
                                    Enabled
                                  </Chip>
                                </Box>
                              )
                            )}
                          </>
                        ) : onboardingData.sso_provider ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                          >
                            <Typography level="body-md" fontWeight="bold">
                              SSO Provider:
                            </Typography>
                            <Chip variant="solid" color="primary" size="md">
                              {onboardingData.sso_provider === "None"
                                ? "Not Configured"
                                : onboardingData.sso_provider}
                            </Chip>
                          </Box>
                        ) : (
                          <Typography
                            level="body-sm"
                            sx={{ color: "text.secondary" }}
                          >
                            No SSO providers configured
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}
            </Stack>
          </CardContent>
        </Card>

        {/* Edit Configuration Section - Only show buttons for pages the user has access to */}
        <Card sx={{ mb: 3 }} variant="outlined">
          <CardContent>
            <Typography level="h4" sx={{ mb: 2 }}>
              ✏️ Need to Make Changes?
            </Typography>
            <Typography level="body-md" sx={{ mb: 3, textAlign: "left" }}>
              You can go back and update any of your configuration settings
              before accessing the application.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
              sx={{ gap: 2 }}
            >
              {isWorkdayAdmin && (
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<EditIcon />}
                  onClick={() => navigate({ to: "/onboarding/workday" })}
                >
                  View Workday Details
                </Button>
              )}
              {isItAdmin && (
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<EditIcon />}
                  onClick={() => navigate({ to: "/onboarding/domain" })}
                >
                  Edit Domains
                </Button>
              )}
              {isSsoAdmin && (
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<EditIcon />}
                  onClick={() => navigate({ to: "/onboarding/sso" })}
                >
                  Edit SSO
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Next Steps */}
        {allStagesComplete && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                📝 What's Next?
              </Typography>
              <Stack spacing={1} sx={{ textAlign: "left" }}>
                <Typography level="body-md">
                  <strong>1. Access Your Application:</strong> Click the "Access
                  Rooster Application" button above to login to your tenant
                </Typography>
                <Typography level="body-md">
                  <strong>2. Complete Setup:</strong> Some configuration
                  settings may need additional details that can be completed in
                  your admin settings
                </Typography>
                <Typography level="body-md">
                  <strong>3. Team Training:</strong> Share the application URL
                  with your team members and provide them with their login
                  credentials
                </Typography>
                <Typography level="body-md">
                  <strong>4. Support:</strong> Contact our support team if you
                  need any assistance with your configuration
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ gap: 2 }}
        >
          {previousPage && (
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => navigate({ to: previousPage })}
              size="lg"
              startDecorator={<ArrowBackIcon />}
              sx={{ minWidth: 200 }}
            >
              Back to {previousPageLabel}
            </Button>
          )}
          {allStagesComplete && roosterUrl && (
            <Button
              component="a"
              href={roosterUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              endDecorator={<LaunchIcon />}
              sx={{ minWidth: 200 }}
            >
              Go to Rooster
            </Button>
          )}
          {allStagesComplete ? (
            <Button
              variant="outlined"
              onClick={handleFinish}
              size="lg"
              sx={{ minWidth: 200 }}
            >
              Finish Setup
            </Button>
          ) : (
            <Tooltip title="Waiting for all configuration stages to complete">
              <span>
                <Button
                  variant="outlined"
                  disabled
                  size="lg"
                  sx={{ minWidth: 200 }}
                >
                  Finish Setup
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export const completionRoute = createRoute({
  getParentRoute: () => onboardingRootRoute,
  path: "/completion",
  component: CompletionPage
});
